import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, User, Menu, X, ChevronDown, LogOut, Settings } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface Category { id: number; name: string; slug: string; children: Category[]; }

// Split categories into two rows when there are many
function splitCategoriesForRows(categories: Category[], maxPerRow = 7): [Category[], Category[]] {
  if (categories.length <= maxPerRow) return [categories, []];
  const mid = Math.ceil(categories.length / 2);
  return [categories.slice(0, mid), categories.slice(mid)];
}

export default function Navbar() {
  const { itemCount, toggleCart } = useCart();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get('/categories/menu').then(r => setCategories(r.data)).catch(() => {});
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setUserMenuOpen(false);
    setMobileOpen(false);
  };

  const hasManyCats = categories.length > 7;
  const [row1, row2] = splitCategoriesForRows(categories);

  const NavLink = ({ cat }: { cat: Category }) => (
    <div className="relative group">
      <Link
        to={`/category/${cat.slug}`}
        className="text-gray-800 text-[11px] font-semibold tracking-widest uppercase hover:text-gray-500 transition-colors flex items-center gap-0.5 whitespace-nowrap py-1"
      >
        {cat.name}
        {cat.children?.length > 0 && <ChevronDown size={11} className="mt-0.5" />}
      </Link>
      {cat.children?.length > 0 && (
        <div className="absolute top-full left-0 bg-white shadow-2xl border border-gray-100 min-w-[180px] py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-1 group-hover:translate-y-0 z-50">
          {cat.children.map(child => (
            <Link key={child.id} to={`/category/${child.slug}`} className="block px-4 py-2.5 text-[11px] text-gray-700 hover:bg-gray-50 hover:text-gray-900 uppercase tracking-wider font-medium">
              {child.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Announcement Bar */}
      <div className="bg-blue-600 text-white text-[11px] text-center py-2 px-4 font-medium tracking-widest">
        FREE DELIVERY ON ORDERS ABOVE RS. 10,000 &nbsp;|&nbsp; CALL / WHATSAPP: 03363898930
      </div>

      <nav className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${isScrolled ? 'shadow-lg' : 'shadow-sm border-b border-gray-100'}`}>

        {/* TOP ROW: Logo + Search/User/Cart */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className={`flex items-center ${hasManyCats ? 'justify-between h-14' : 'h-16 md:h-[68px]'}`}>

            {/* Left: Hamburger (mobile) + Logo */}
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 -ml-2 text-gray-700 hover:text-gray-900" aria-label="Open menu">
                <Menu size={22} />
              </button>
              <Link to="/" className="flex items-center gap-2 flex-shrink-0 group">
                <div className="w-8 h-8 md:w-9 md:h-9 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-md group-hover:bg-blue-600 transition-colors">
                  <span className="text-white text-[12px] font-black">BC</span>
                </div>
                <div className="leading-tight">
                  <span className="block text-[18px] md:text-[21px] font-black tracking-[0.05em] text-gray-900">BabyCub</span>
                  <span className="block text-[7px] md:text-[8px] font-semibold tracking-[0.25em] text-blue-400 uppercase -mt-0.5">Baby Wear</span>
                </div>
              </Link>
            </div>

            {/* Center: Desktop nav (single row when few categories) */}
            {!hasManyCats && (
              <div className="hidden lg:flex items-center gap-5 xl:gap-7">
                <Link to="/special-prices" className="text-red-600 text-[11px] font-bold tracking-widest uppercase hover:text-red-700 transition-colors whitespace-nowrap">Sale</Link>
                <Link to="/new-in" className="text-gray-800 text-[11px] font-semibold tracking-widest uppercase hover:text-gray-500 transition-colors whitespace-nowrap">New Arrivals</Link>
                {categories.map(cat => <NavLink key={cat.id} cat={cat} />)}
                <Link to="/refund-policy" className="text-gray-800 text-[11px] font-semibold tracking-widest uppercase hover:text-gray-500 transition-colors whitespace-nowrap">Returns</Link>
                <Link to="/contact" className="text-gray-800 text-[11px] font-semibold tracking-widest uppercase hover:text-gray-500 transition-colors whitespace-nowrap">Contact</Link>
              </div>
            )}

            {/* Right: Icons */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button onClick={() => setSearchOpen(v => !v)} className="p-2 text-gray-700 hover:text-gray-900 transition-colors" aria-label="Search">
                <Search size={19} />
              </button>
              <div className="relative" ref={userMenuRef}>
                <button onClick={() => setUserMenuOpen(v => !v)} className="p-2 text-gray-700 hover:text-gray-900 transition-colors" aria-label="Account">
                  <User size={19} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 bg-white shadow-2xl border border-gray-100 w-52 py-1 z-50 rounded-xl overflow-hidden">
                    {isAuthenticated ? (
                      <>
                        <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                          <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                          <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        </div>
                        <Link to="/account" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                          <User size={15} className="text-gray-400" /> My Account
                        </Link>
                        {isAdmin && (
                          <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                            <Settings size={15} className="text-gray-400" /> Admin Panel
                          </Link>
                        )}
                        <div className="border-t border-gray-100 mt-1">
                          <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full text-left">
                            <LogOut size={15} className="text-red-400" /> Sign Out
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <Link to="/login" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 font-medium">Sign In</Link>
                        <Link to="/register" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">Create Account</Link>
                      </>
                    )}
                  </div>
                )}
              </div>
              <button onClick={toggleCart} className="relative p-2 text-gray-700 hover:text-gray-900 transition-colors" aria-label="Cart">
                <ShoppingBag size={19} />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {itemCount > 9 ? '9+' : itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* SECOND ROW: Category nav when many categories */}
        {hasManyCats && (
          <div className="hidden lg:block border-t border-gray-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <div className="flex items-center justify-center gap-5 xl:gap-7 py-2">
                <Link to="/special-prices" className="text-red-600 text-[11px] font-bold tracking-widest uppercase hover:text-red-700 transition-colors whitespace-nowrap">Sale</Link>
                <Link to="/new-in" className="text-gray-800 text-[11px] font-semibold tracking-widest uppercase hover:text-gray-500 transition-colors whitespace-nowrap">New Arrivals</Link>
                {row1.map(cat => <NavLink key={cat.id} cat={cat} />)}
              </div>
              {row2.length > 0 && (
                <div className="flex items-center justify-center gap-5 xl:gap-7 pb-2">
                  {row2.map(cat => <NavLink key={cat.id} cat={cat} />)}
                  <Link to="/refund-policy" className="text-gray-800 text-[11px] font-semibold tracking-widest uppercase hover:text-gray-500 transition-colors whitespace-nowrap">Returns</Link>
                  <Link to="/contact" className="text-gray-800 text-[11px] font-semibold tracking-widest uppercase hover:text-gray-500 transition-colors whitespace-nowrap">Contact</Link>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Search overlay */}
        {searchOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg z-50">
            <div className="max-w-2xl mx-auto px-4 py-4">
              <form onSubmit={handleSearch} className="flex items-center gap-3">
                <Search size={18} className="text-gray-400 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search for products..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="flex-1 text-sm outline-none text-gray-900 placeholder-gray-400"
                />
                <button type="button" onClick={() => setSearchOpen(false)} className="text-gray-400 hover:text-gray-700 transition-colors">
                  <X size={18} />
                </button>
              </form>
            </div>
          </div>
        )}
      </nav>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="absolute top-0 left-0 h-full w-[min(80vw,320px)] bg-white shadow-2xl flex flex-col animate-slide-in-left">
            {/* Drawer Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <Link to="/" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[11px] font-black">BC</span>
                </div>
                <span className="text-[16px] font-black tracking-[0.05em] text-gray-900">BabyCub</span>
              </Link>
              <button onClick={() => setMobileOpen(false)} className="p-1 text-gray-600 hover:text-gray-900">
                <X size={20} />
              </button>
            </div>

            {/* Drawer Nav */}
            <div className="flex-1 overflow-y-auto py-4 px-3">
              <Link to="/special-prices" onClick={() => setMobileOpen(false)} className="flex items-center px-3 py-3 text-red-600 text-sm font-bold uppercase tracking-wider hover:bg-red-50 rounded-lg mb-0.5">Sale</Link>
              <Link to="/new-in" onClick={() => setMobileOpen(false)} className="flex items-center px-3 py-3 text-gray-800 text-sm font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-lg mb-0.5">New Arrivals</Link>
              {categories.map(cat => (
                <div key={cat.id}>
                  <Link to={`/category/${cat.slug}`} onClick={() => setMobileOpen(false)} className="flex items-center px-3 py-3 text-gray-800 text-sm font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-lg mb-0.5">
                    {cat.name}
                  </Link>
                  {cat.children?.length > 0 && (
                    <div className="ml-4 border-l-2 border-gray-100 pl-3 mb-1">
                      {cat.children.map(child => (
                        <Link key={child.id} to={`/category/${child.slug}`} onClick={() => setMobileOpen(false)} className="block px-2 py-2 text-gray-600 text-xs uppercase tracking-wider hover:text-gray-900 hover:bg-gray-50 rounded">
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="border-t border-gray-100 mt-3 pt-3">
                <Link to="/refund-policy" onClick={() => setMobileOpen(false)} className="flex items-center px-3 py-3 text-gray-800 text-sm font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-lg mb-0.5">Returns</Link>
                <Link to="/contact" onClick={() => setMobileOpen(false)} className="flex items-center px-3 py-3 text-gray-800 text-sm font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-lg mb-0.5">Contact</Link>
                <Link to="/about" onClick={() => setMobileOpen(false)} className="flex items-center px-3 py-3 text-gray-800 text-sm font-semibold uppercase tracking-wider hover:bg-gray-50 rounded-lg mb-0.5">About</Link>
              </div>
              <div className="border-t border-gray-100 mt-3 pt-3">
                {isAuthenticated ? (
                  <>
                    <Link to="/account" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-gray-800 text-sm font-semibold hover:bg-gray-50 rounded-lg mb-0.5">
                      <User size={16} className="text-gray-400" /> My Account
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-gray-800 text-sm font-semibold hover:bg-gray-50 rounded-lg mb-0.5">
                        <Settings size={16} className="text-gray-400" /> Admin Panel
                      </Link>
                    )}
                    <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-3 text-red-600 text-sm font-semibold hover:bg-red-50 rounded-lg w-full text-left">
                      <LogOut size={16} className="text-red-400" /> Sign Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-gray-800 text-sm font-semibold hover:bg-gray-50 rounded-lg mb-0.5">
                      <User size={16} className="text-gray-400" /> Sign In
                    </Link>
                    <Link to="/register" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-3 text-gray-800 text-sm font-semibold hover:bg-gray-50 rounded-lg">
                      Create Account
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
